import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    getUserPods,
    getUserSubscriptions,
    checkPastDueInvoices,
    userIsLoadedSelector,
    userPodsIsLoadedSelector,
    userPodsSelector,
    userSubscriptionsIsLoadedSelector,
    userSubscriptionsSelector,
    userHasPastDueInvoicesSelector,
    pastDueInvoicesCheckedSelector,
} from 'redux/reducers/user';
import { AthletePod, AthleteSubscription } from '../app.types';
import { useAppDispatch } from '../redux/store';
import { Button, Snackbar } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useHistory, useLocation } from 'react-router-dom';
import athleteUpdatePaymentMethod from '../api/Auth/athleteUpdatePaymentMethod';
import { getFromLocalStorageWithExpiry, setInLocalStorageWithExpiry } from '../utils/localStorage';
import { useSegmentAnalytics } from './SegmentAnalyticsContext';
import { userSelector } from 'redux/reducers/user';

interface Props {
    subscriptions: AthleteSubscription[];
    subscriptionsLoaded: boolean;
    userPods: AthletePod[];
    userPodsIsLoaded: boolean;
    isModalOpen: boolean;
    closeModal: () => void;
    hasPastDueInvoices: boolean; // New property
}

export const AthleteContext = React.createContext<Props>({
    subscriptions: [],
    subscriptionsLoaded: false,
    userPods: [],
    userPodsIsLoaded: false,
    isModalOpen: false,
    closeModal: () => {},
    hasPastDueInvoices: false, // New property
});

export function AthleteSubscriptionsProvider({ children }: { children: React.ReactNode }) {
    const subscriptions = useSelector(userSubscriptionsSelector);
    const subscriptionsLoaded = useSelector(userSubscriptionsIsLoadedSelector);
    const userLoaded = useSelector(userIsLoadedSelector);
    const userSubscriptionsIsLoaded = useSelector(userSubscriptionsIsLoadedSelector);
    const userPods = useSelector(userPodsSelector);
    const userPodsIsLoaded = useSelector(userPodsIsLoadedSelector);
    const hasPastDueInvoices = useSelector(userHasPastDueInvoicesSelector); // New selector
    const pastDueInvoicesChecked = useSelector(pastDueInvoicesCheckedSelector); // New selector
    const dispatch = useAppDispatch();

    const [isTransferRequestModalOpen, setIsTransferRequestModalOpen] = useState(false);
    const [isTransferRequestModalClosedInSession, setIsTransferRequestModalClosedInSession] =
        useState(false);
    const [isPastDueAthleteSubscriptionModalOpen, setIsPastDueAthleteSubscriptionModalOpen] =
        useState(false);
    const [
        isPastDueAthleteSubscriptionModalClosedInSession,
        setIsPastDueAthleteSubscriptionModalClosedInSession,
    ] = useState(false);
    // New state for past due invoices modal
    const [isPastDueInvoiceModalOpen, setIsPastDueInvoiceModalOpen] = useState(false);
    const [isPastDueInvoiceModalClosedInSession, setIsPastDueInvoiceModalClosedInSession] =
        useState(false);
    const [hasDefaultSubscription, setHasDefaultSubscription] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('1Tul Subscription Required');
    const [dialogContent, setDialogContent] = useState(
        'You will need to sign up for a 1Tul subscription. Please see your marketplaces and choose one from which to purchase a subscription.',
    );
    const location = useLocation();
    const [snackMessage, setSnackMessage] = useState<string>('');
    const userData = useSelector(userSelector).userData;
    const { track } = useSegmentAnalytics();

    const closeTransferRequestModal = () => {
        setIsTransferRequestModalOpen(false);
        setIsTransferRequestModalClosedInSession(true);
    };

    const handleChangeBillingMethod = () => {
        setInLocalStorageWithExpiry('athleteUpdatePaymentMethodAcknowledged', true, 60);

        if (userData?.uuid) {
            track('AthleteContext.handleChangeBillingMethod', {
                subscriptions: subscriptions,
                user: userData,
            });
        }

        athleteUpdatePaymentMethod(window.location.href)
            .then((response) => {
                closePastDueAthleteSubscriptionModal();
                closePastDueInvoiceModal();
                window.location.href = response.data.url;
            })
            .catch((e: any) => {
                setSnackMessage(`Error creating checkout session. ${e.response?.data?.message}`);
            });
    };

    const closePastDueAthleteSubscriptionModal = () => {
        setInLocalStorageWithExpiry('athleteUpdatePaymentMethodAcknowledged', true, 60);
        if (userData?.uuid) {
            track('AthleteContext.closePastDueAthleteSubscriptionModal', {
                subscriptions: subscriptions,
                user: userData,
            });
        }
        setIsPastDueAthleteSubscriptionModalOpen(false);
        setIsPastDueAthleteSubscriptionModalClosedInSession(true);
    };

    // New handler for past due invoices
    const closePastDueInvoiceModal = () => {
        setInLocalStorageWithExpiry('athleteUpdateInvoicePaymentAcknowledged', true, 24);
        setIsPastDueInvoiceModalOpen(false);
        setIsPastDueInvoiceModalClosedInSession(true);
    };

    const history = useHistory();

    const handleVisitInvoices = () => {
        closePastDueInvoiceModal();
        history.push('/athletes');
    };

    const ModalComponent = () => (
        <>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={!!snackMessage}
                autoHideDuration={6000}
                message={snackMessage}
                onClose={() => setSnackMessage('')}
            />
            <Dialog
                open={isTransferRequestModalOpen}
                onClose={() => setIsTransferRequestModalOpen(false)}
            >
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{dialogContent}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            history.push('/athletes');
                            setIsTransferRequestModalOpen(false);
                        }}
                    >
                        View My Marketplaces
                    </Button>
                    <Button variant="outlined" onClick={() => setIsTransferRequestModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isPastDueAthleteSubscriptionModalOpen}
                onClose={closePastDueAthleteSubscriptionModal}
            >
                <DialogTitle>Athlete Subscription Payment Error</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        One or more of your athlete subscriptions are past due. Please update your
                        payment information on Stripe as soon as possible to avoid any service
                        interruptions.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleChangeBillingMethod}>
                        Update Payment Information
                    </Button>
                    <Button variant="outlined" onClick={closePastDueAthleteSubscriptionModal}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            {/* New Dialog for Past Due Invoices */}
            <Dialog open={isPastDueInvoiceModalOpen} onClose={closePastDueInvoiceModal}>
                <DialogTitle>Invoice Payment Required</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have one or more past due invoices. Please update your payment
                        information on Stripe as soon as possible to avoid any service
                        interruptions.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleVisitInvoices}>
                        Pay Invoices
                    </Button>
                    <Button variant="outlined" onClick={closePastDueInvoiceModal}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    useEffect(() => {
        if (userLoaded && !userSubscriptionsIsLoaded) {
            dispatch(getUserSubscriptions()).then((subscriptions: any) => {
                if (
                    subscriptions.meta.requestStatus === 'fulfilled' &&
                    subscriptions.payload.some((subscription: any) => {
                        return (
                            subscription.name === 'default' || subscription.name === 'athlete_combo'
                        );
                    })
                ) {
                    setHasDefaultSubscription(true);
                } else {
                    setHasDefaultSubscription(false);

                    if (!location.pathname.includes('/athletes')) {
                        // setIsTransferRequestModal(true); @todo this is commented because this modal annoys users.
                    }
                }
            });
        }

        /**
         * Check for past due invoices after user is loaded
         */
        if (userLoaded && !pastDueInvoicesChecked) {
            dispatch(checkPastDueInvoices());
        }

        /**
         * Pod transfer modal notification logic.
         */
        if (userLoaded && hasDefaultSubscription && !userPodsIsLoaded) {
            dispatch(getUserPods()).then((pods: any) => {
                if (pods.payload?.length > 0) {
                    /**
                     * To display a pod transfer pending modal notification, there must be at least one pod
                     * that has a null transfer_request_accepted_at and there must be no pods
                     * where transfer_request_accepted_at is not null.
                     */
                    const hasPendingPodTransferRequest =
                        pods.payload.some(
                            (pod: any) => pod.transfer_request_accepted_at === null,
                        ) &&
                        !pods.payload.some((pod: any) => pod.transfer_request_accepted_at !== null);
                    if (
                        hasPendingPodTransferRequest &&
                        !isTransferRequestModalClosedInSession &&
                        !location.pathname.includes('/athletes')
                    ) {
                        console.debug('Pod transfer request(s) found');
                        setIsTransferRequestModalOpen(true);
                        setDialogTitle('Pod Transfer Request');
                        setDialogContent(
                            'You have a FiyrPod transfer request. Please see your marketplaces and choose one from which to purchase a subscription.',
                        );
                    }
                }
            });
        }

        /**
         * Athlete subscription payment error modal notification logic.
         */
        if (userLoaded && userSubscriptionsIsLoaded) {
            const hasPastDueSubscription = subscriptions.some(
                (subscription) => subscription.stripe_status === 'past_due',
            );
            if (
                hasPastDueSubscription &&
                !isPastDueAthleteSubscriptionModalClosedInSession &&
                getFromLocalStorageWithExpiry('athleteUpdatePaymentMethodAcknowledged') !== true
            ) {
                setIsPastDueAthleteSubscriptionModalOpen(true);
                if (userData?.uuid) {
                    track('AthleteContext.setIsPastDueAthleteSubscriptionModalOpen(true)', {
                        subscriptions: subscriptions,
                        user: userData,
                    });
                }
            }
        }

        /**
         * Past due invoices modal notification logic.
         */
        if (userLoaded && pastDueInvoicesChecked) {
            if (
                hasPastDueInvoices &&
                !isPastDueInvoiceModalClosedInSession &&
                getFromLocalStorageWithExpiry('athleteUpdateInvoicePaymentAcknowledged') !== true
            ) {
                setIsPastDueInvoiceModalOpen(true);
            }
        }
    }, [
        track,
        userData,
        location.pathname,
        userLoaded,
        userSubscriptionsIsLoaded,
        dispatch,
        userPodsIsLoaded,
        userPods,
        isTransferRequestModalClosedInSession,
        subscriptions,
        isPastDueAthleteSubscriptionModalClosedInSession,
        hasDefaultSubscription,
        pastDueInvoicesChecked,
        hasPastDueInvoices,
        isPastDueInvoiceModalClosedInSession,
    ]);

    return (
        <AthleteContext.Provider
            value={{
                subscriptions,
                subscriptionsLoaded,
                userPods,
                userPodsIsLoaded,
                isModalOpen: isTransferRequestModalOpen,
                closeModal: closeTransferRequestModal,
                hasPastDueInvoices, // Only include this flag
            }}
        >
            {children}
            <ModalComponent />
        </AthleteContext.Provider>
    );
}
